import React, { useMemo } from 'react'
import { StyleProp, ViewStyle } from 'react-native'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { IconName } from '@src/components/base'
import { EhrAppointmentAttendanceChargeKind, EhrAppointmentStateKind } from '@src/types'
import { insurancePolicySelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { userSelector } from '@src/selectors/app'
import { useIsDarkMode } from '@src/config/theme'
import { Chip } from './Chip'

interface ChipProperties {
  label: string
  icon: IconName
  style: ViewStyle
  weight?: 'fill' | 'bold'
}

interface AppointmentPaymentMethodChipProps {
  lastStateKind: EhrAppointmentStateKind | null | undefined
  chargeKind: EhrAppointmentAttendanceChargeKind
  style?: StyleProp<ViewStyle>
}

export const AppointmentPaymentMethodChip = ({
  lastStateKind,
  chargeKind,
  style: containerStyle,
}: AppointmentPaymentMethodChipProps) => {
  const styles = useStyleSheet(themedStyles)
  const user = useSelector(userSelector)
  const primaryCoachingKind = user?.primaryCoachingKind
  const insurancePolicy = useSelector(insurancePolicySelector)
  const showInsuranceUi = primaryCoachingKind?.insuranceReimbursable === true
  const darkMode = useIsDarkMode()

  const { label, icon, style, weight = 'fill' } = useMemo((): ChipProperties => {
    if (chargeKind === EhrAppointmentAttendanceChargeKind.BsFree) {
      return { label: 'Free', icon: 'gift', style: styles.covered, weight: 'bold' }
    }

    if (chargeKind === EhrAppointmentAttendanceChargeKind.BsAllowanceCovered) {
      return { label: 'Covered', icon: 'star', style: styles.covered }
    }

    if (
      !insurancePolicy ||
      chargeKind === EhrAppointmentAttendanceChargeKind.NsPaid ||
      chargeKind === EhrAppointmentAttendanceChargeKind.BsPaid
    ) {
      return { label: 'Self-Pay', icon: 'credit-card', style: styles.selfPay }
    }

    if (chargeKind === EhrAppointmentAttendanceChargeKind.BsInsuranceCovered) {
      if (lastStateKind === EhrAppointmentStateKind.InReview || insurancePolicy.inReview) {
        return {
          label: 'In Review',
          icon: 'shield-star',
          style: darkMode ? styles.inReviewDark : styles.inReview,
        }
      }
      return { label: 'Covered', icon: 'shield-check', style: styles.covered }
    }

    if (insurancePolicy.declined) {
      return { label: 'Ineligible', icon: 'shield-slash', weight: 'bold', style: styles.uncovered }
    }

    return { label: 'Uncovered', icon: 'shield-slash', style: styles.uncovered, weight: 'bold' }
  }, [lastStateKind, chargeKind, insurancePolicy, darkMode, styles])

  if (!showInsuranceUi) {
    return null
  }

  return <Chip icon={icon} label={label} style={[containerStyle, style]} weight={weight} />
}

const themedStyles = StyleService.create({
  selfPay: {
    backgroundColor: 'theme.surface.base0',
  },
  inReview: {
    backgroundColor: 'theme.info.base',
  },
  inReviewDark: {
    backgroundColor: 'theme.info.darkest',
  },
  covered: {
    backgroundColor: 'theme.success.darkest',
  },
  uncovered: {
    backgroundColor: 'theme.warning.base',
  },
})
